import React from 'react';
import toast from 'react-hot-toast';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Ikey, LicenseBackend } from '../../types.type';
import { transformDateString } from '../../util';
import { managementService } from '../../service/management.service';
import { homeService } from '../../../home/service/home.service';
import { useThrottle } from '../../../hooks/useThrottle';
import { ReleaseButton } from '../release-button.component';
import { CopyableKey } from '../copyable-key.component';

interface IProps {
  data: Ikey[];
}

const colTemplate = '32px minmax(80px, 150px) 82px 185px 112px 52px 88px';

// A LicenseSpring row's type is a BILLING period; a Squarebit row's is a
// SOURCE. Same question, different words - so the namespace follows the
// backend rather than translating "stripe" as if it were a billing period and
// falling through to the raw word.
const typeWords = (backend: LicenseBackend | undefined, type: string) =>
  backend === 'squarebit'
    ? `management.table.tier.${type.toLowerCase()}`
    : `subscription.licenseType.${type.toLowerCase()}`;

//: Squarebit sources, coloured on the same logic as the billing types above:
//: a paid licence green, everything else quiet.
const sourceColors: Record<string, string> = {
  stripe: 'bg-badge-green-bg/60 border-badge-green-text/30 text-badge-green-text'
};

const TypeBadge = ({
  type,
  subscriptionStatus,
  cancelAtPeriodEnd,
  backend
}: {
  type: string;
  subscriptionStatus?: string | null;
  cancelAtPeriodEnd?: boolean;
  backend?: LicenseBackend;
}) => {
  const { t } = useTranslation();
  // Stripe subscription state belongs to LicenseSpring rows. A Squarebit
  // licence carries no subscription at all, so it can never read "cancelled".
  const isCancelled =
    backend !== 'squarebit' &&
    (cancelAtPeriodEnd || subscriptionStatus === 'canceled');

  if (isCancelled) {
    return (
      <span className="inline-block px-[9px] py-[3px] rounded-[5px] border bg-badge-red-bg border-badge-red-text/40 text-badge-red-text font-mono text-[10px] tracking-[0.06em] uppercase w-fit">
        {t('subscription.cancelled')}
      </span>
    );
  }

  const colors: Record<string, string> = {
    trial: 'bg-badge-red-bg/60   border-badge-red-text/30   text-badge-red-text',
    monthly: 'bg-badge-green-bg/60 border-badge-green-text/30 text-badge-green-text',
    yearly: 'bg-badge-green-bg/60 border-badge-green-text/30 text-badge-green-text'
  };
  const palette = backend === 'squarebit' ? sourceColors : colors;
  const style =
    palette[type.toLowerCase()] ??
    'bg-surface-secondary border-border text-content-muted';

  return (
    <span
      className={`inline-block px-[9px] py-[3px] rounded-[5px] border font-mono text-[10px] tracking-[0.06em] uppercase w-fit ${style}`}
    >
      {t(typeWords(backend, type), { defaultValue: type })}
    </span>
  );
};

const SeatsBadge = ({
  used,
  max,
  stripeId,
  row
}: {
  used: number;
  max: number;
  stripeId: string | null;
  row: Ikey;
}) => {
  const { t } = useTranslation();
  // Two servers, two ways to buy a seat. LicenseSpring rows go to the Stripe
  // billing portal for the subscription behind them; Squarebit rows start a
  // checkout that names THIS licence, so the webhook tops it up instead of
  // minting a second key. Only a licence that can actually be topped up gets
  // a live pill - a comped or free one is not for sale by the seat.
  const squarebit = row.backend === 'squarebit';
  const topUpable = squarebit && row.status === 'active' && row.source === 'stripe';

  const updatePortal = useMutation({
    mutationKey: ['subscriptionUpdatePortal', stripeId, row.licenseId],
    mutationFn: () =>
      squarebit
        ? (homeService.getCheckoutSession(
            'perpetual',
            row.productSlug,
            { version: '', locale: '', price: '' },
            1,
            row.licenseId!
          ) as Promise<string>)
        : managementService.getSubscriptionUpdateUrl(stripeId!),
    onSuccess: (url) => {
      window.location.href = url;
    },
    onError: () => toast.error(t('management.table.seats.updateError'))
  });
  const throttled = useThrottle(updatePortal.mutate);

  const label = `${used}/${max}`;

  if (squarebit ? !topUpable : !stripeId) {
    return (
      <span className="min-w-[36px] h-[24px] rounded-full bg-border-alt font-mono text-[11px] text-content-muted-alt flex items-center justify-center mx-auto px-[6px]">
        {label}
      </span>
    );
  }

  return (
    <button
      type="button"
      title={t('management.table.seats.updateTitle')}
      onClick={() => throttled()}
      className="min-w-[36px] h-[24px] rounded-full bg-blue text-surface-base font-mono text-[11px] font-semibold flex items-center justify-center mx-auto px-[6px] shadow-[0_2px_6px_rgba(14,165,201,0.35)] transition-all hover:bg-blue-deep-1 hover:shadow-[0_3px_10px_rgba(14,165,201,0.45)] hover:scale-110 cursor-pointer"
    >
      {label}
    </button>
  );
};

export const ManagementTableDesktop = ({ data }: IProps) => {
  const { t } = useTranslation();

  return (
    <div className="w-full max-w-[819px] min-w-0">
      <div className="font-mono text-[9.5px] tracking-[0.14em] uppercase text-content-muted-light mb-[13px]">
        {t('management.table.heading')}
      </div>

      <div
        className="grid gap-x-[10px] px-[14px] pb-[8px] font-mono text-[9.5px] tracking-widest uppercase text-content-muted-light justify-items-center"
        style={{ gridTemplateColumns: colTemplate }}
      >
        <span>{t('management.table.columns.index')}</span>
        <span>{t('management.table.columns.product')}</span>
        <span>{t('management.table.columns.type')}</span>
        <span>{t('management.table.columns.keyCode')}</span>
        <span>{t('management.table.columns.validUntil')}</span>
        <span>{t('management.table.columns.seats')}</span>
        <span></span>
      </div>

      <div className="flex flex-col gap-[8px]">
        {data.map((row, index) => {
          const {
            key,
            productName,
            expiresAt,
            type,
            maxActivations,
            usedActivations,
            subscriptionStatus,
            cancelAtPeriodEnd,
            stripeId
          } = row;
          // A Squarebit perpetual licence has no expiry at all. An empty
          // cell reads as missing data; the word reads as the fact it is.
          const date = expiresAt
            ? transformDateString(expiresAt)
            : t('management.table.perpetual');
          const num = String(index + 1).padStart(2, '0');

          return (
            <div
              key={key}
              className="grid items-center justify-items-center gap-x-[10px] px-[14px] py-[13px] bg-surface-section border border-border-dark/[0.11] rounded-[10px] transition-all hover:border-[rgba(14,165,201,0.3)] hover:shadow-[0_2px_12px_rgba(14,165,201,0.06)]"
              style={{ gridTemplateColumns: colTemplate }}
            >
              <span className="font-mono text-[12px] text-content-muted-light">
                {num}
              </span>
              <span className="font-body text-[13.5px] font-medium text-content-dark">
                {productName}
              </span>
              <TypeBadge
                type={type}
                subscriptionStatus={subscriptionStatus}
                cancelAtPeriodEnd={cancelAtPeriodEnd}
                backend={row.backend}
              />
              <CopyableKey value={key} className="justify-self-stretch" />
              <span className="font-body text-[13px] text-content-muted-alt">
                {date}
              </span>
              <SeatsBadge
                used={usedActivations}
                max={maxActivations}
                stripeId={stripeId}
                row={row}
              />
              <ReleaseButton row={row} />
            </div>
          );
        })}
      </div>
    </div>
  );
};
